import { useState } from 'react';
import { NavLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { PanelLeftClose, PanelLeftOpen, Icon, type LucideIcon } from '../ui/Icon';
import type { ModeNav, NavItem } from './modeNav';

// Kept from when only agencies had a sidebar: the fold a user chose survives.
const COLLAPSED_KEY = 'gromly.agencySidebar.collapsed';

function readCollapsed(): boolean {
  try { return localStorage.getItem(COLLAPSED_KEY) === '1'; } catch { return false; }
}

interface Props {
  title: string;
  /** The agency's logo; without one, `icon` on a tinted square. */
  logoUrl?: string | null;
  icon: LucideIcon;
  nav: ModeNav;
}

/** ADR-0036: the agency's or the admin's screens, desktop only. Folds to icons and remembers it per device. */
export default function ModeSidebar({ title, logoUrl, icon, nav }: Props) {
  const { t } = useTranslation();
  const [collapsed, setCollapsed] = useState(readCollapsed);

  function toggle() {
    const next = !collapsed;
    setCollapsed(next);
    try { localStorage.setItem(COLLAPSED_KEY, next ? '1' : '0'); } catch { /* lasts for this tab only */ }
  }

  const link = ({ to, icon: itemIcon, labelKey }: NavItem) => (
    <NavLink key={to} to={to} end={to === '/dashboard'} title={collapsed ? t(labelKey) : undefined} aria-label={collapsed ? t(labelKey) : undefined}
      className={({ isActive }) =>
        `flex items-center gap-2.5 h-9 rounded-lg text-sm font-medium transition-colors ${collapsed ? 'justify-center' : 'px-2.5'} ${
          isActive
            ? 'bg-teal-50 dark:bg-teal-900/30 text-teal-700 dark:text-teal-300'
            : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-100 hover:bg-slate-100 dark:hover:bg-slate-800'
        }`
      }>
      <Icon as={itemIcon} size={17} className="shrink-0" />
      {!collapsed && <span className="truncate">{t(labelKey)}</span>}
    </NavLink>
  );

  return (
    <aside aria-label={title}
      className={`hidden md:flex flex-col shrink-0 bg-white dark:bg-slate-900 border-r border-slate-200/70 dark:border-slate-800 transition-[width] duration-150 ${collapsed ? 'w-16' : 'w-56'}`}>
      <div className={`flex items-center gap-2.5 h-16 shrink-0 ${collapsed ? 'justify-center' : 'px-4'}`}>
        {logoUrl
          ? <img src={logoUrl} alt="" className="w-8 h-8 rounded-lg object-contain bg-white border border-slate-200 dark:border-slate-700 shrink-0" />
          : (
            <span className="w-8 h-8 rounded-lg bg-teal-50 dark:bg-teal-900/30 flex items-center justify-center shrink-0">
              <Icon as={icon} size={16} className="text-teal-600 dark:text-teal-400" />
            </span>
          )}
        {!collapsed && <span className="text-sm font-semibold text-slate-900 dark:text-slate-100 truncate">{title}</span>}
      </div>

      <nav className="flex-1 overflow-y-auto px-2 space-y-0.5">{nav.main.map(link)}</nav>

      <div className="px-2 py-2 space-y-0.5 border-t border-slate-100 dark:border-slate-800">
        {nav.footer.map(link)}
        <button type="button" onClick={toggle} aria-expanded={!collapsed}
          aria-label={t(collapsed ? 'nav.expandMenu' : 'nav.collapseMenu')} title={t(collapsed ? 'nav.expandMenu' : 'nav.collapseMenu')}
          className={`w-full flex items-center gap-2.5 h-9 rounded-lg text-sm text-slate-400 dark:text-slate-500 hover:text-slate-700 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors cursor-pointer ${collapsed ? 'justify-center' : 'px-2.5'}`}>
          <Icon as={collapsed ? PanelLeftOpen : PanelLeftClose} size={17} className="shrink-0" />
          {!collapsed && <span>{t('nav.collapseMenu')}</span>}
        </button>
      </div>
    </aside>
  );
}
